import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Shield, Search, CheckCircle, XCircle, Download } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CertificateValidation = () => {
  const [certificateId, setCertificateId] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleValidation = async () => {
    if (!certificateId.trim()) {
      alert('Please enter a certificate ID');
      return;
    }

    setIsLoading(true);
    setError(null);
    setValidationResult(null);

    try {
      // Call PSQC certificate verification API
      const response = await axios.post(`${BACKEND_URL}/api/psqc-certificate/verify`, {
        certificate_no: certificateId.trim()
      });

      if (response.data.success && response.data.certificate) {
        // Certificate found
        setValidationResult({
          isValid: true,
          certificateId: response.data.certificate.certificate_no,
          name: response.data.certificate.participant_name,
          course: response.data.certificate.certificate_name,
          issueDate: response.data.certificate.awarded_date,
          downloadLink: response.data.certificate.download_link
        });
      } else {
        // Certificate not found
        setValidationResult({
          isValid: false,
          certificateId,
          message: response.data.message || 'Certificate not found'
        });
      }
    } catch (err) {
      console.error('Certificate verification error:', err);
      setError('Unable to verify certificate. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="validate" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-12 space-y-4"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div 
              className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4"
              initial={{ scale: 0, rotate: -180 }}
              animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
              transition={{ duration: 0.6, type: "spring", bounce: 0.4 }}
            >
              <Shield className="h-10 w-10 text-white" />
            </motion.div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Validate Your <span className="text-blue-600">Certificate</span>
            </h2>
            <p className="text-xl text-gray-600">
              Enter your certificate ID to verify its authenticity
            </p>
          </motion.div>

          {/* Validation Card */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="shadow-2xl border-2 border-blue-100">
              <CardHeader>
                <CardTitle className="text-2xl">Certificate Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="Enter Certificate ID (e.g., ISDE-2025-001)"
                    value={certificateId}
                    onChange={(e) => setCertificateId(e.target.value)}
                    className="flex-1 h-12 text-lg border-2 focus:border-blue-600 transition-colors"
                    onKeyPress={(e) => e.key === 'Enter' && handleValidation()}
                  />
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      onClick={handleValidation}
                      disabled={isLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 transition-all"
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <motion.div 
                            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                          Validating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Search className="h-5 w-5" />
                          Validate
                        </span>
                      )}
                    </Button>
                  </motion.div>
                </div>

                {/* Error Display */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Alert className="border-2 border-red-300 bg-red-50">
                      <div className="flex items-start gap-4">
                        <XCircle className="h-6 w-6 text-red-600 mt-1" />
                        <AlertDescription>
                          <h4 className="font-bold text-lg text-red-800 mb-2">
                            Error
                          </h4>
                          <p className="text-sm text-gray-700">{error}</p>
                        </AlertDescription>
                      </div>
                    </Alert>
                  </motion.div>
                )}

                {/* Validation Result */}
                {validationResult && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Alert
                      className={`border-2 ${
                        validationResult.isValid
                          ? 'border-green-300 bg-green-50'
                          : 'border-red-300 bg-red-50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", bounce: 0.5 }}
                        >
                          {validationResult.isValid ? (
                            <CheckCircle className="h-6 w-6 text-green-600 mt-1" />
                          ) : (
                            <XCircle className="h-6 w-6 text-red-600 mt-1" />
                          )}
                        </motion.div>
                        <div className="flex-1">
                          <AlertDescription>
                            {validationResult.isValid ? (
                              <motion.div 
                                className="space-y-3"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                              >
                                <h4 className="font-bold text-lg text-green-800">
                                  ✓ Certificate Valid
                                </h4>
                                <div className="space-y-2 text-sm text-gray-700">
                                  <div className="grid grid-cols-2 gap-2">
                                    <span className="font-semibold">Certificate ID:</span>
                                    <span>{validationResult.certificateId}</span>
                                    <span className="font-semibold">Participant Name:</span>
                                    <span>{validationResult.name}</span>
                                    <span className="font-semibold">Certificate Name:</span>
                                    <span>{validationResult.course}</span>
                                    <span className="font-semibold">Awarded Date:</span>
                                    <span>{validationResult.issueDate}</span>
                                  </div>
                                  {validationResult.downloadLink && (
                                    <div className="mt-4">
                                      <a
                                        href={validationResult.downloadLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                                      >
                                        <Download className="h-4 w-4" />
                                        Download Certificate
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            ) : (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                              >
                                <h4 className="font-bold text-lg text-red-800 mb-2">
                                  ✕ Certificate Not Found
                                </h4>
                                <p className="text-sm text-gray-700">
                                  The certificate ID you entered could not be verified. Please check the ID and try again or contact us at training@isde.academy for assistance.
                                </p>
                              </motion.div>
                            )}
                          </AlertDescription>
                        </div>
                      </div>
                    </Alert>
                  </motion.div>
                )}

                {/* Help Text */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Need Help?</strong> If you're having trouble validating your certificate, please contact us at <a href="tel:04449591335" className="text-blue-600 hover:underline">044 49591335</a> or email <a href="mailto:training@isde.academy" className="text-blue-600 hover:underline">training@isde.academy</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
